import { WorkOutlineOutlined as WorkOutlineOutlinedIcon } from 'components/icons';
import Loader from 'components/Loader';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import LineClamp from 'components/misc/LineClamp.tsx';
import ListClamp from 'components/misc/ListClamp.tsx';
import PartyName from 'components/party/PartyName';
import type { DataTableColumnDef } from 'components/tables/data-table';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import HoursDisplay from 'components/util/HoursDisplay';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useLazyGetEffortsCountQuery, useLazyGetEffortsWithFiltersQuery } from 'store/api/efforts';
import { useStartEffortExportMutation } from 'store/api/exports';
import { EffortListItem } from 'types/efforts';
import { EXPORT_CONFIG } from 'types/exports';
import { PaginatedFieldPassiveFilter } from 'utils/filterUtil.ts';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { secondsToHours } from 'utils/time';
import { useAsyncExport } from 'utils/useAsyncExport.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import {
    EFFORT_LIST_CLASSIFIERS,
    type FilterGroup,
    type FilterId,
    useEffortFilterConfig,
} from './useEffortFilterConfig';

/** The list's count, with the drafts it lists. */
const useLazyEffortsCountWithDraftsQuery: typeof useLazyGetEffortsCountQuery = (options) => {
    const [count, ...rest] = useLazyGetEffortsCountQuery(options);
    return [useWithDrafts(count), ...rest];
};

const getEffortHref = (effort: EffortListItem) => `/effort/${effort.id}`;

const EffortsTable = ({
    contactId,
    matterId,
    passiveFilter = '',
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
}: {
    contactId?: string;
    matterId?: string;
    passiveFilter?: string;
    disabledGroups?: FilterGroup[];
    disabledFilterIds?: FilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<FilterId>[];
}): JSX.Element => {
    const navigateWithReturn = useNavigateWithReturnState();

    // ONE route for both the header button and the in-place empty-state CTA: on a contact or case
    // tab the new effort must stay associated with it, and two copies of this choice would drift.
    const createEffortUrl = contactId
        ? `/contact/${contactId}/effort/new`
        : matterId
          ? `/matter/${matterId}/effort/new`
          : '/effort/new';
    const { displayDecimalScale } = useTimecardPrecision();

    // Build default filters for efforts (my efforts by default)
    const initialSearchParams = 'qf=effortByEmployee.id=ismyeffort=true';

    const [fetchEfforts, { data, isFetching }] = useLazyGetEffortsWithFiltersQuery();
    const fetchData = useWithDrafts(fetchEfforts);

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: EFFORT_LIST_CLASSIFIERS });

    const hasPermissionToExportEfforts = useHasPermissions(['EXPORT.EFFORTS']);

    const { isBusy: isExportDataFetching, triggerManualDownload } = useAsyncExport(useStartEffortExportMutation, {
        entityType: 'EFFORT',
        entityName: t('efforts.export.entity-name'),
        fileName: EXPORT_CONFIG.EFFORT.fileName,
    });

    const columns = useMemo<DataTableColumnDef<EffortListItem>[]>(
        () => [
            {
                header: t('bd.date-activity-description'),
                accessorKey: 'effortDate',
                enableSorting: false,
                meta: {
                    // This column measures ~4x each of the other four (which render equal-width), and
                    // the ratio is stable across resolutions.
                    widthWeight: 4,
                    minPx: 320,
                },
                cell: ({ cell }) => {
                    const { id, effortDate, effortActivityTypeCode, description, isDraft } = cell.row.original;
                    return (
                        <div className="flex flex-col gap-2">
                            {effortDate ?? '-'}
                            {isDraft ? <DraftChip /> : null}
                            <div className="flex items-start justify-between gap-1">
                                <RouterLink
                                    className="entity-link w-fit font-semibold whitespace-normal no-underline hover:underline"
                                    to={`/effort/${id}`}
                                >
                                    <p className="text-body1 font-semibold whitespace-nowrap">
                                        {/* A draft may have no activity type yet. */}
                                        {(effortActivityTypeCode &&
                                            getClassifierText(
                                                classifiers?.['EFFORT_ACTIVITY_TYPE'],
                                                effortActivityTypeCode
                                            )) ||
                                            t('efforts.bd-effort')}
                                    </p>
                                </RouterLink>
                                {/* The list has no actions column, so a draft's menu sits by its name. */}
                                {isDraft ? <DraftRowMenu draft={{ type: 'bd-effort', id }} className="size-7" /> : null}
                            </div>
                            <div className="flex flex-col">
                                <LineClamp keepIndentation>{description}</LineClamp>
                            </div>
                        </div>
                    );
                },
            },
            {
                header: t('bd.by-employee'),
                accessorKey: 'effortsByEmployees',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: ({ cell }) => {
                    const { effortsByEmployees } = cell.row.original;
                    return (
                        <ListClamp
                            items={effortsByEmployees}
                            getKey={({ id }) => id}
                            renderItem={(item) => (
                                <PartyName party={item} isLink variant="list" className="whitespace-normal" />
                            )}
                        />
                    );
                },
            },
            {
                header: t('bd.for-person'),
                accessorKey: 'effortsForPeople',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: ({ cell }) => {
                    const { effortsForPeople } = cell.row.original;
                    return (
                        <ListClamp
                            items={effortsForPeople}
                            getKey={({ id }) => id}
                            renderItem={(item) => (
                                <PartyName
                                    party={item}
                                    isLink
                                    variant="list"
                                    showPartyNumber
                                    className="whitespace-normal"
                                />
                            )}
                        />
                    );
                },
            },
            {
                header: t('bd.for-entities'),
                accessorKey: 'effortsForCompanies',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: ({ cell }) => {
                    const { effortsForCompanies } = cell.row.original;
                    return (
                        <ListClamp
                            items={effortsForCompanies}
                            getKey={({ id }) => id}
                            renderItem={(item) => (
                                <PartyName
                                    party={item}
                                    isLink
                                    variant="list"
                                    showPartyNumber
                                    className="whitespace-normal"
                                />
                            )}
                        />
                    );
                },
            },
            {
                header: t('bd.used-time'),
                accessorKey: 'usedTimeInSeconds',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: ({ cell }) => {
                    const { usedTimeInSeconds } = cell.row.original;
                    // Only a draft has no time yet.
                    return usedTimeInSeconds == null ? (
                        '—'
                    ) : (
                        <div className="flex flex-col">
                            <HoursDisplay value={secondsToHours(usedTimeInSeconds, displayDecimalScale)} />
                        </div>
                    );
                },
            },
        ],
        [classifiers, displayDecimalScale]
    );

    const filterConfig = useEffortFilterConfig({
        classifiers,
        passiveFilter,
        disabledGroups,
        disabledFilterIds,
        paginatedFieldPassiveFilters,
    });

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<EffortListItem>
            getRowHref={getEffortHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="efforts"
            listKey="efforts"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="efforts.efforts"
            singularTextId="effort"
            noEntriesId="efforts.no-efforts-found"
            noEntriesBodyId="efforts.empty.body"
            noEntriesIcon={WorkOutlineOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.EFFORTS']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn(createEffortUrl)}
                    labelId="efforts.add-new-effort"
                    newLabelId="new-entity.bd-effort"
                />
            }
            fetchData={fetchData}
            fetchCount={useLazyEffortsCountWithDraftsQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
            disableUrlState={false}
            extraHeaderComponent={
                contactId || matterId ? (
                    <ListCreateButton
                        permissions={['CREATE.EFFORTS']}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => navigateWithReturn(createEffortUrl)}
                        labelId="efforts.add-new-effort"
                        newLabelId="new-entity.bd-effort"
                    />
                ) : null
            }
            exportHookResults={
                hasPermissionToExportEfforts
                    ? [
                          {
                              labelKey: 'export',
                              isBusy: isExportDataFetching,
                              triggerManualDownload,
                          },
                      ]
                    : undefined
            }
            initialSearchParams={initialSearchParams}
        />
    );
};

export default EffortsTable;
